import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse
} from 'axios';
import { useDispatch } from 'react-redux';
import {
  Action,
  AnyAction
} from 'redux';
import { multiClientMiddleware } from 'redux-axios-middleware';
import { ThunkDispatch } from 'redux-thunk';

import config from '../config';
import { State } from '../state';

// these are just the defaults from redux-axios-middleware
export const SUFFIX_SUCCESS = '_SUCCESS';
export const SUFFIX_FAILURE = '_FAIL';

const CLIENTS_CONFIGURED = {
  backend: {
    client: axios.create({
      baseURL: config.backend.url,
      responseType: 'json'
    })
  },
  coreService: {
    client: axios.create({
      baseURL: config.coreService.url + '/api',
      responseType: 'json'
    })
  },
  customerProfile: {
    client: axios.create({
      baseURL: config.customerProfile.url + '/api',
      responseType: 'json'
    })
  },
  workManagement: {
    client: axios.create({
      baseURL: config.workManagement.url + '/api',
      responseType: 'json'
    })
  }
};
/** A possible api client which requests can be made to. */
export type Client = keyof typeof CLIENTS_CONFIGURED;

// Action Types
// see https://github.com/svrcekmichal/redux-axios-middleware for documentation

/** An action which results in an axios request. */
export interface AxiosAction<TResponseData> {
  type: string;
  payload: {
    /** The client to which the request will be made. */
    client: Client;
    /** The request config. */
    request: AxiosRequestConfig;
  };
  onSuccess?: (responseData: TResponseData) => (dispatch: ThunkDispatch<{}, {}, AnyAction>) => void;
}
/** An action which is dispatched when an AxiosAction is successful. */
export interface AxiosSuccessAction<TPreviousAction extends AxiosAction<any>> {
  type: `${TPreviousAction['type']}${typeof SUFFIX_SUCCESS}`;
  /** The axios response object. */
  payload: TPreviousAction extends AxiosAction<infer TResponseData>
    ? AxiosResponse<TResponseData>
    : never;
  meta: { previousAction: TPreviousAction };
}
/** An action which is dispatched when an AxiosAction is unsuccessful. */
export interface AxiosFailureAction<TPreviousAction extends AxiosAction<any>> {
  type: `${TPreviousAction['type']}${typeof SUFFIX_FAILURE}`;
  /** The axios error object. */
  error: AxiosError;
  meta: { previousAction: TPreviousAction };
}
/** The possible resulting actions from an axios action being dispatched. */
export type AxiosResultingAction<TPreviousAction extends AxiosAction<any>> =
  AxiosSuccessAction<TPreviousAction> | AxiosFailureAction<TPreviousAction>;

/**
 * The redux dispatch method, with the correct return type for axios actions, as
 * dispatching an axios action returns a promise which resolves to the resulting
 * action.
 */
export type AxiosDispatch = <TAction extends Action = AnyAction>(action: TAction) => TAction extends AxiosAction<any>
  ? TAction & Promise<AxiosResultingAction<TAction>>
  : TAction;

// Utilities

/** Given an (axios) action type, returns its success action's type. */
export const getSuccessActionType = <TAction extends AxiosAction<any>>(
  type: TAction['type']
): AxiosSuccessAction<TAction>['type'] => `${type}${SUFFIX_SUCCESS}`;

/** Given an (axios) action type, returns its failure action's type. */
export const getFailureActionType = <TAction extends AxiosAction<any>>(
  type: TAction['type']
): AxiosFailureAction<TAction>['type'] => `${type}${SUFFIX_FAILURE}`;

/**
 * Given an axios action and its redux action type, returns a function which can
 * be used to check whether a redux action is the success-action of that which
 * was specified.
 */
export function isSuccessActionOf<TAction extends AxiosAction<any>>(type: TAction['type']) {
  return function isSuccess(action: AnyAction): action is AxiosSuccessAction<TAction> {
    const success = getSuccessActionType(type);
    return action.type === success;
  };
}

/**
 * A wrapper around react-redux' useDispatch which has the correct return type for
 * axios actions.
 * See https://redux.js.org/usage/usage-with-typescript#typing-the-usedispatch-hook.
 */
export function useAxiosDispatch( ) {
  const dispatch: AxiosDispatch = useDispatch( );
  return dispatch;
}

// Middleware

interface InterceptorParams {
  getState: () => State;
  dispatch: any;
  getSourceAction: any;
}

/**
 * The request interceptor which adds a bearer token to requests when the current
 * user is authenticated.
 */
export const bearerTokenInterceptor = async (
  { getState }: InterceptorParams,
  interceptorConfig: any
) => {
  const authState = getState().auth;

  if (authState.authenticated) {
    const token = await authState.getTokenFunction();
    interceptorConfig.headers.common.authorization = `Bearer ${token}`;
  }
  return interceptorConfig;
};

/** The redux middleware to support axios actions. */
export const axiosMiddleware = multiClientMiddleware(CLIENTS_CONFIGURED, {
  interceptors: {
    request: [
      bearerTokenInterceptor
    ]
  }
});
